/**
 * Service prices grid — per-customer pricing for job service definitions.
 *
 * Server-backed via EntityGrid over odl-app/job-service-prices/. Scoped to a
 * single customer via filter[customer_id]. Columns come from the backend
 * grid-spec descriptor; FE field overrides preserve the child-services chip.
 * Inline cell editing (installer_pay, customer_charge, boolean flags) is wired
 * via options.gridProps so edits patch the price record in place.
 * New-price and delete dialogs are preserved and wired to refresh the grid
 * after mutation. Rows expose "id" as identifier (BE remaps serviceid→id).
 *
 * Contains:
 * - NewServicePriceDialog — create a price entry linking a service to a customer
 * - ServicePricesGrid     — EntityGrid over odl-app/job-service-prices/ (default export)
 */

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import FilterBar from '@sophocles/components/FilterBar';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { EntityGrid } from '@sophocles/data-grids';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import {
  useCreateJobServicePriceMutation,
  usePatchJobServicePriceMutation,
  useRemoveJobServicePriceMutation,
} from '@sophocles/features/entities/jobServicePrices';
import { useLazyListJobServicesQuery } from '@sophocles/features/entities/jobServices';
import { addToast } from '@sophocles/features/toast';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import { serviceTypeOptions } from '@sophocles/views/services/utils/serviceTypes';

function NewServicePriceDialog({ customerId, open, onClose, onSubmit }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [services, setServices] = useState([]);

  const [createJobServicePrice] = useCreateJobServicePriceMutation();
  const [fetchJobServices] = useLazyListJobServicesQuery();

  const NewPriceValidationSchema = useMemo(
    () =>
      yup.object({
        service_id: yup.string().required(t('projectServices.servicePrices.validation.serviceRequired')),
        installer_pay: yup.number().required(t('projectServices.servicePrices.validation.installerPayRequired')),
        customer_charge: yup.number().required(t('projectServices.servicePrices.validation.customerChargeRequired')),
        parts_included: yup.bool(),
        labor_included: yup.bool(),
        account_extra_pay: yup.bool(),
      }),
    [t],
  );

  useEffect(() => {
    if (!open) return;
    fetchJobServices({ limit: 200 }).then(res => {
      if (res.data?.results) setServices(res.data.results);
    });
  }, [fetchJobServices, open]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: NewPriceValidationSchema,
    initialValues: {
      customer_id: customerId,
      service_id: '',
      installer_pay: 0,
      customer_charge: 0,
      parts_included: false,
      labor_included: false,
      account_extra_pay: false,
    },
    onSubmit: async values => {
      const result = await createJobServicePrice({ body: values });
      if (result.error) {
        const errors = result.error.data?.errors ?? [];
        dispatch(
          addToast({
            title: t('projectServices.servicePrices.toast.error'),
            message:
              errors.length > 0
                ? t('projectServices.servicePrices.toast.createFailedWithErrors', { errors: errors.join('; ') })
                : t('projectServices.servicePrices.toast.createFailed'),
            level: 'error',
            dismissible: true,
          }),
        );
      } else {
        dispatch(
          addToast({
            title: t('projectServices.servicePrices.toast.success'),
            message: t('projectServices.servicePrices.toast.pricingCreated'),
            level: 'success',
            dismissible: true,
          }),
        );
        // eslint-disable-next-line no-use-before-define -- formik <-> handleClose form a submit-time cycle
        handleClose();
        if (onSubmit) onSubmit();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const canSubmit = !formik.isSubmitting && Object.keys(formik.errors).length === 0;

  return (
    <ResponsiveDialog
      title={t('projectServices.servicePrices.dialog.newTitle')}
      open={open}
      canSubmit={canSubmit}
      onClose={handleClose}
      onSubmit={formik.submitForm}
      sx={{
        '& .MuiDialogContent-root > :not(style):not(:last-child)': { mb: 2 },
        '& .MuiDialogContent-root': { py: 3 },
      }}
    >
      <FormControl fullWidth size="small">
        <InputLabel id="service-select-label">{t('projectServices.servicePrices.form.service')}</InputLabel>
        <Select
          labelId="service-select-label"
          label={t('projectServices.servicePrices.form.service')}
          name="service_id"
          value={formik.values.service_id}
          onChange={formik.handleChange}
        >
          {services.map(service => (
            <MenuItem key={service.serviceid ?? service.id} value={service.serviceid ?? service.id}>
              {service.name} ({service.service_type})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        required
        fullWidth
        name="installer_pay"
        label={t('projectServices.servicePrices.form.installerPay')}
        type="number"
        size="small"
        value={formik.values.installer_pay}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.installer_pay && Boolean(formik.errors.installer_pay)}
        helperText={formik.touched.installer_pay && formik.errors.installer_pay}
      />
      <TextField
        required
        fullWidth
        name="customer_charge"
        label={t('projectServices.servicePrices.form.customerCharge')}
        type="number"
        size="small"
        value={formik.values.customer_charge}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.customer_charge && Boolean(formik.errors.customer_charge)}
        helperText={formik.touched.customer_charge && formik.errors.customer_charge}
      />
      <Box>
        <FormControlLabel
          control={
            <Checkbox name="parts_included" checked={formik.values.parts_included} onChange={formik.handleChange} />
          }
          label={t('projectServices.servicePrices.form.partsIncluded')}
        />
        <FormControlLabel
          control={
            <Checkbox name="labor_included" checked={formik.values.labor_included} onChange={formik.handleChange} />
          }
          label={t('projectServices.servicePrices.form.laborIncluded')}
        />
        <FormControlLabel
          control={
            <Checkbox
              name="account_extra_pay"
              checked={formik.values.account_extra_pay}
              onChange={formik.handleChange}
            />
          }
          label={t('projectServices.servicePrices.form.accountCrewExtraPay')}
        />
      </Box>
    </ResponsiveDialog>
  );
}

function ServicePricesGrid({ customerId }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [patchJobServicePrice] = usePatchJobServicePriceMutation();
  const [removeJobServicePrice] = useRemoveJobServicePriceMutation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = usePersistedState(useGridSessionKey('servicePricesTable', 'typeFilter'), null);

  // customer_id scopes this grid to the current customer page.
  // service_type is the enum filter exposed in the filter bar.
  const extraParams = useMemo(() => {
    const params = { customer_id: customerId };
    if (typeFilter) params.service_type = typeFilter;
    return params;
  }, [customerId, typeFilter]);

  const handleTypeFilterChange = useCallback(
    value => {
      setTypeFilter(value);
    },
    [setTypeFilter],
  );

  // child_services chip — BE emits child_services_count (int) at the row root;
  // the nested service.child_services array is never populated in this endpoint.
  const fieldOverrides = useMemo(
    () => ({
      child_services: {
        renderCell: ({ row }) => {
          const childCount = row.child_services_count ?? 0;
          if (childCount === 0) return '-';
          return <Chip label={`+${childCount} auto`} size="small" variant="outlined" color="secondary" />;
        },
      },
    }),
    [],
  );

  // Delete action — not modeled in the descriptor
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => (
          <Tooltip title={t('projectServices.servicePrices.tooltip.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={e => {
                e.stopPropagation();
                confirmDelete(row, {
                  entityLabel: t('general.price'),
                  onConfirm: async deletedRow => {
                    await removeJobServicePrice({ id: deletedRow.id });
                    dispatch(
                      addToast({
                        title: t('projectServices.servicePrices.toast.success'),
                        message: t('projectServices.servicePrices.toast.pricingDeleted'),
                        level: 'success',
                        dismissible: true,
                      }),
                    );
                    refreshGrid();
                  },
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [confirmDelete, dispatch, removeJobServicePrice, t, refreshGrid],
  );

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const changed = {};
      for (const field of [
        'installer_pay',
        'customer_charge',
        'parts_included',
        'labor_included',
        'account_extra_pay',
      ]) {
        if (newRow[field] !== oldRow[field]) {
          changed[field] = newRow[field];
        }
      }

      if (Object.keys(changed).length === 0) return oldRow;

      const result = await patchJobServicePrice({ id: newRow.id, body: changed });
      if (result.error) {
        dispatch(
          addToast({
            title: t('projectServices.servicePrices.toast.error'),
            message: t('projectServices.servicePrices.toast.failedToUpdatePricing'),
            level: 'error',
            dismissible: true,
          }),
        );
        return oldRow;
      }
      return { ...oldRow, ...result.data };
    },
    [dispatch, patchJobServicePrice, t],
  );

  const handleProcessRowUpdateError = useCallback(
    error => {
      dispatch(
        addToast({
          title: t('projectServices.servicePrices.toast.error'),
          message: t('projectServices.servicePrices.toast.updateFailed', { message: error.message }),
          level: 'error',
          dismissible: true,
        }),
      );
    },
    [dispatch, t],
  );

  const typeFilterOptions = useMemo(() => serviceTypeOptions(t), [t]);

  const quickFilters = useMemo(
    () => [
      {
        key: 'service_type',
        label: t('projectServices.services.columns.serviceType', 'Type'),
        type: 'single',
        allLabel: t('projectServices.servicePrices.types.all', 'All Types'),
        options: typeFilterOptions,
        value: typeFilter,
        onChange: value => handleTypeFilterChange(value ?? null),
      },
    ],
    [t, typeFilterOptions, typeFilter, handleTypeFilterChange],
  );

  const handleResetAll = useCallback(() => handleTypeFilterChange(null), [handleTypeFilterChange]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  return (
    <Fragment>
      {deleteConfirmDialog}
      <EntityGrid
        basePath="job-service-prices"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('projectServices.servicePrices.pageTitle'),
          persistKey: 'servicePricesTable',
          toolbarVariant: 'flat',
          toolbarContent,
          createAction: {
            label: t('projectServices.servicePrices.dialog.newTitle'),
            onClick: () => setIsNewDialogOpen(true),
          },
          extraColumns,
          onGridReady: handleGridReady,
          countNoun: t('projectServices.servicePrices.countNoun', 'prices'),
          gridProps: {
            editMode: 'cell',
            processRowUpdate,
            onProcessRowUpdateError: handleProcessRowUpdateError,
            sx: {
              '& .MuiDataGrid-cell--editable': {
                '&:hover': { outline: '1px solid', outlineColor: 'primary.main', outlineOffset: -1 },
              },
            },
          },
        }}
      />

      <NewServicePriceDialog
        customerId={customerId}
        open={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
        onSubmit={() => {
          setIsNewDialogOpen(false);
          refreshGrid();
        }}
      />
    </Fragment>
  );
}

export default ServicePricesGrid;
